import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  PlusIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';

function formatDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not available'
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
};

function AdminProfiles() {
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(API_URLS.admin.admins);
      setAdmins(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError(err.response?.data?.message || 'Failed to load admin profiles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const filteredAdmins = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return admins;
    return admins.filter((admin) => [
      admin.firstName,
      admin.lastName,
      admin.email,
      admin.phone,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [admins, searchTerm]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post(API_URLS.admin.users, {
        ...form,
        userType: 'admin',
      });
      setShowCreateModal(false);
      setForm(emptyForm);
      setSuccess('Admin account created successfully.');
      await fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create admin account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Profiles</h1>
          <p className="mt-2 text-sm text-gray-600">
            Church transport admins who can access this portal. Create accounts for new team members.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center self-start rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Add admin
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div role="status" className="mb-4 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          {success}
        </div>
      )}

      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <label htmlFor="admin-search" className="sr-only">Search admins</label>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            id="admin-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, email, or phone"
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" aria-label="Loading admin profiles" />
        </div>
      ) : filteredAdmins.length === 0 ? (
        <p className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">No admin accounts match your search.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">{filteredAdmins.length} admin(s)</p>
          {filteredAdmins.map((admin) => (
            <article key={admin.id} className="rounded-lg bg-white p-5 shadow">
              <div className="flex items-start gap-3">
                <UserCircleIcon className="h-10 w-10 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {admin.firstName} {admin.lastName}
                  </h2>
                  <p className="text-sm text-gray-500">Joined {formatDate(admin.createdAt)} · Status: {admin.status || 'active'}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                      <a href={`mailto:${admin.email}`} className="text-indigo-700 hover:text-indigo-900">{admin.email}</a>
                    </span>
                    {admin.phone && (
                      <span className="inline-flex items-center gap-1">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        <a href={`tel:${admin.phone}`} className="text-indigo-700 hover:text-indigo-900">{admin.phone}</a>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <section className="mt-8 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5 sm:p-6">
        <h2 className="text-sm font-semibold text-gray-900">Account management</h2>
        <p className="mt-1 text-sm text-gray-600">
          Edit, suspend, or remove admin accounts is planned in a follow-up release. Use All Users for urgent changes until then.
        </p>
      </section>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="create-admin-title">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 id="create-admin-title" className="text-lg font-semibold text-gray-900">Create admin account</h2>
            <p className="mt-1 text-sm text-gray-600">The new user can sign in immediately with the password you set.</p>
            <form onSubmit={handleCreateAdmin} className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First name</label>
                  <input id="firstName" name="firstName" required value={form.firstName} onChange={handleFormChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last name</label>
                  <input id="lastName" name="lastName" required value={form.lastName} onChange={handleFormChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                <input id="email" name="email" type="email" required value={form.email} onChange={handleFormChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Temporary password</label>
                <input id="password" name="password" type="password" required minLength={8} value={form.password} onChange={handleFormChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone (optional)</label>
                <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleFormChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreateModal(false)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? 'Creating…' : 'Create admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProfiles;
